// AsientoFormXL - Sprint 44 Fase 3 Vista 8.
// Form contenedor de asiento con header (badge estado + totales DR/CR/Dif),
// card datos (fecha, descripcion, referencia) y LineasPanelXL.
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import useAsientoFormXL from '../hooks/useAsientoFormXL';
import LineasPanelXL from './LineasPanelXL';
import XLBadge from '../../../components/xl/XLBadge';
import XLInput from '../../../components/xl/XLInput';
import XLSkeleton from '../../../components/xl/XLSkeleton';
import { fmt, ESTADO_VARIANT, ESTADO_LABEL } from '../utils';

const CSS_ID = 'xl-asiento-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__back:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub {
  margin: 2px 0 0; font-size: 12px;
  color: var(--xl-color-text-secondary);
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.xl-hdr__sub b { color: var(--xl-color-text); font-family: var(--xl-font-mono) }
.xl-hdr__dif--ok  { color: var(--xl-color-success) }
.xl-hdr__dif--bad { color: var(--xl-color-danger) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center }

.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--success { background: var(--xl-color-success); color: var(--xl-color-text-inverse) }
.xl-btn--success:hover { filter: brightness(1.08) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 18px;
  margin-bottom: 16px;
}
.xl-grid {
  display: grid; grid-template-columns: 1fr 2fr; gap: 14px;
}
@media (max-width: 720px) { .xl-grid { grid-template-columns: 1fr } }
.xl-grid__full { grid-column: 1 / -1 }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

export default function AsientoFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const fm = useAsientoFormXL();

  if (!fm.loaded) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={20} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const isEditable = fm.form.estado === 'borrador' && !fm.isNew;
  const balanceado = Math.abs(Number(fm.form.diferencia) || 0) < 0.01;
  const difCls = 'xl-hdr__dif ' + (balanceado ? 'xl-hdr__dif--ok' : 'xl-hdr__dif--bad');

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => fm.navigate('/contabilidad/asientos')}>{'\u2190 Volver'}</button>
          <div>
            <h2 className="xl-hdr__title">
              {fm.isNew ? 'Nuevo asiento' : (fm.form.numero || 'Asiento borrador')}
            </h2>
            <p className="xl-hdr__sub">
              <XLBadge variant={ESTADO_VARIANT[fm.form.estado] || 'neutral'} dot>
                {ESTADO_LABEL[fm.form.estado] || fm.form.estado}
              </XLBadge>
              <span>{fm.form.modulo_origen || 'manual'}</span>
              <span>DR <b>{fmt(fm.form.total_debito)}</b></span>
              <span>CR <b>{fmt(fm.form.total_credito)}</b></span>
              <span>Dif <b className={difCls}>{fmt(fm.form.diferencia)}</b></span>
            </p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {(isEditable || fm.isNew) && (
            <button type="button" className="xl-btn xl-btn--primary"
              onClick={fm.onSave} disabled={fm.saving}>
              {fm.saving ? 'Guardando\u2026' : (fm.isNew ? 'Crear borrador' : 'Guardar')}
            </button>
          )}
          {isEditable && (
            <button type="button" className="xl-btn xl-btn--success"
              onClick={fm.onPublicar}
              disabled={fm.publishing || !balanceado}
              title={!balanceado ? 'Asiento no cuadra' : 'Publicar (bloquea edicion)'}>
              {fm.publishing ? 'Publicando\u2026' : 'Publicar'}
            </button>
          )}
        </div>
      </header>

      {fm.msg && (
        <div className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}>
          {fm.msg.text}
        </div>
      )}

      <div className="xl-card">
        <div className="xl-grid">
          <XLInput label="Fecha" type="date"
            disabled={!isEditable && !fm.isNew}
            value={fm.form.fecha || ''}
            onChange={(e) => fm.set('fecha', e.target.value)} />
          <XLInput label="Descripcion"
            disabled={!isEditable && !fm.isNew}
            value={fm.form.descripcion || ''}
            onChange={(e) => fm.set('descripcion', e.target.value)}
            placeholder="Ej: Venta VTA-001 cliente X" />
          <div className="xl-grid__full">
            <XLInput label="Referencia"
              disabled={!isEditable && !fm.isNew}
              value={fm.form.referencia || ''}
              onChange={(e) => fm.set('referencia', e.target.value)}
              placeholder="venta:123" />
          </div>
        </div>
      </div>

      {!fm.isNew && (
        <div className="xl-card" style={{ padding: '20px' }}>
          <LineasPanelXL asientoId={fm.id} isEditable={isEditable} onChanged={fm.refresh} />
        </div>
      )}
    </div>
  );
}
